import Constants from '../../core/nqs/Services';
import Core from '../../core/Core';
import SessionsRequest from '../SessionsRequest';
import { Method, SessionsTag } from '../../common/Constants';
import CoreConstants from '../../core/utils/CoreConstants';
import Log from '../../common/log';
export default class SessionsRequestHandler {
    /**
     * Npaw Communication implements an abstraction layer over API requests.
     * Internally, Communication implements queues of {@link Request} objects.
     * This queue can be blocked using {@link Transform}
     *
     * @param options - Optional configuration options
     */
    constructor(options) {
        this.readyToSend = false;
        this.servicesToWait = [Constants.Service.SESSION_START];
        this.tokenCheckInterval = null;
        this.maxWaitingRequests = 25;
        this.onWillSendAnalyticsRequestListeners = [];
        /**
         * Queue of {@link SessionsRequest}
         */
        this._requests = [];
        this._waitingForToken = [];
        // Set maxWaitingRequests from options if provided
        if (options && typeof options.maxWaitingRequests === 'number') {
            this.maxWaitingRequests = options.maxWaitingRequests;
        }
    }
    destroy() {
        this._requests = [];
        this._waitingForToken = [];
        this.readyToSend = false;
        this.clearTokenCheckInterval();
    }
    shouldSendStats() {
        return this.readyToSend;
    }
    /**
     * Update the maximum waiting requests limit
     * @param maxWaitingRequests - New maximum limit
     */
    updateMaxWaitingRequests(maxWaitingRequests) {
        if (typeof maxWaitingRequests === 'number' && maxWaitingRequests > 0) {
            this.maxWaitingRequests = maxWaitingRequests;
        }
    }
    /**
     * Clear the token check interval if it exists
     */
    clearTokenCheckInterval() {
        if (this.tokenCheckInterval !== null) {
            clearInterval(this.tokenCheckInterval);
            this.tokenCheckInterval = null;
        }
    }
    /**
     * Start the token check interval to monitor when session token becomes available
     */
    startTokenCheckInterval() {
        if (this.tokenCheckInterval === null) {
            this.tokenCheckInterval = setInterval(() => {
                if (Core.getInstance().getFastDataSessionToken() !== '') {
                    Log.notice(SessionsTag, 'Session token is now available, processing waiting requests');
                    this.clearTokenCheckInterval();
                    this.processWaitingRequests();
                }
            }, 1000);
        }
    }
    /**
     * Process all waiting requests when token becomes available
     */
    processWaitingRequests() {
        if (this._waitingForToken.length === 0) {
            return;
        }
        this._waitingForToken.forEach((request) => this.prepareParams(request));
        this._requests.push(...this._waitingForToken);
        this._waitingForToken = [];
        // Process all the requests that were waiting
        this._processRequests(true);
    }
    /**
     * Enqueues the provided request
     *
     * @param { SessionsRequest } request Request to be enqueued
     */
    sendRequest(request, sessionActive) {
        if (Core.getInstance().getFastDataSessionToken() === '') {
            if (this._waitingForToken.length >= this.maxWaitingRequests) {
                Log.warn(SessionsTag, `Too many requests waiting for token (${this._waitingForToken.length}/${this.maxWaitingRequests}), dropping request`);
            }
            else {
                this._waitingForToken.push(request);
                // Start interval only when waitingForToken array size is 0 (first time)
                if (this._waitingForToken.length === 1) {
                    this.startTokenCheckInterval();
                }
            }
            return;
        }
        this._waitingForToken.forEach((request) => this.prepareParams(request));
        this._requests.push(...this._waitingForToken);
        this._waitingForToken = [];
        const params = request.getParams();
        const serviceName = request.getService();
        const listeners = this.onWillSendAnalyticsRequestListeners;
        for (const listener of listeners) {
            listener(serviceName, params);
        }
        this.prepareParams(request);
        this._requests.push(request);
        this._processRequests(sessionActive);
    }
    prepareParams(request) {
        const service = request.getService();
        request.setParam('sessionId', request.getParam('sessionId') || Core.getInstance().getFastDataSessionToken());
        if (service == Constants.Service.SESSION_START) {
            request.setParam('beatTime', request.getParam('beatTime') || Core.getInstance().getFastDataService().getBeatTime());
        }
    }
    /**
     * Build a generic request to the given host.
     *
     * @param {string} service A string with the service to be called. ie...
     * @param {Object} [params] Object of key:value params.
     * @param {Method} method Type of request (GET, POST, ...)
     * @param {any} body body of the request
     * @param {function} [onSuccessCallback] The defined onSuccess callback to the request
     * @param {function} [onFailCallback] The defined onFail callback to the request
     * @param {ExpirationManager} [expirationManager] Expiration Manager
     */
    buildRequest(service, params, method, body, onSuccessCallback, onFailCallback, expirationManager) {
        method = method ? method : Method.GET;
        const request = new SessionsRequest(service, params, body, onSuccessCallback, onFailCallback, expirationManager);
        request.setMethod(method);
        return request;
    }
    /**
     * Execute pending requests in the queue. Returns rejected ones to the queue.
     */
    _processRequests(sessionActive) {
        const workingQueue = this._requests;
        this._requests = [];
        const rejected = [];
        while (workingQueue.length) {
            const request = workingQueue.shift();
            // Apply logic of blocking requests before init or start or if we are in offline mode
            if (sessionActive || request.getService() == Constants.Service.SESSION_START) {
                // this.servicesToWait.indexOf(request.getService()) !== -1) {
                this.readyToSend = true;
            }
            if (this.readyToSend) {
                Core.getInstance().pushData(Core.getInstance().getOptions().offline ? CoreConstants.Consumer.BLACK_HOLE : CoreConstants.Consumer.NQS, request.getService(), request.getMethod(), request.getParams(), request.getOnSuccess(), request.getOnFail());
                const expirationManager = request.getExpirationManager();
                if (expirationManager) {
                    expirationManager.onRequestSent();
                }
                while (rejected.length) {
                    const rejectedRequest = rejected.shift();
                    Core.getInstance().pushData(Core.getInstance().getOptions().offline ? CoreConstants.Consumer.BLACK_HOLE : CoreConstants.Consumer.NQS, rejectedRequest.getService(), rejectedRequest.getMethod(), rejectedRequest.getParams(), rejectedRequest.getOnSuccess(), rejectedRequest.getOnFail());
                }
            }
            else {
                rejected.push(request);
            }
        }
        this._requests.push(...rejected);
    }
}
